import React from "react";
import {
  ArrowTopRightOnSquareIcon,
  LockClosedIcon,
  LockOpenIcon,
} from "@heroicons/react/24/outline";
import * as TooltipPrimitives from "@radix-ui/react-tooltip";
import {
  RICH_TOOLTIP_CONTENT_CLASS_NAME,
  SIMPLE_TOOLTIP_CONTENT_CLASS_NAME,
  TOOLTIP_ARROW_CLASS_NAME,
} from "components/Environment/shared/DisabledActionTooltip";
import type { IApplication } from "models/Workspace.model";
import { getComputeCardActionButtonClassName } from "./computeCardActionStyles";

type EnvironmentApplicationSummary = Pick<
  IApplication,
  "labels" | "name" | "port"
>;

export type EnvironmentApplicationAccess =
  | "public"
  | "authenticated"
  | "inaccessible";
export type EnvironmentApplicationAccessAudience =
  | "any-authenticated"
  | "specified-users";

export interface EnvironmentApplicationChipProps {
  /** Application metadata displayed in the CTA and details popup. */
  application: EnvironmentApplicationSummary;
  /** Resolved endpoint for the application, when it is ready. */
  hostname?: string;
  /** Parent environment name shown in the details popup. */
  environmentName?: string;
  /** Effective access after both application and resolved-port policy checks. */
  access: EnvironmentApplicationAccess;
  /** Authenticated audience used to explain non-public access. */
  accessAudience?: EnvironmentApplicationAccessAudience;
  /** Reason the action cannot be opened even if the endpoint is resolved. */
  disabledReason?: string;
  /** Opens secure-link sharing for the backing port when the viewer can edit it. */
  onShare?: () => void;
  variant?: "chip" | "row";
}

export const getEnvironmentApplicationLabel = (
  application: Pick<IApplication, "labels" | "name">,
) => application.labels?.["cta-value"]?.trim() || application.name;

const getApplicationHref = (hostname: string | undefined) => {
  if (!hostname) return undefined;
  return hostname.startsWith("http") ? hostname : `https://${hostname}`;
};

export const EnvironmentApplicationChip: React.FC<
  EnvironmentApplicationChipProps
> = ({
  application,
  hostname,
  access,
  accessAudience,
  disabledReason,
  onShare,
  variant = "chip",
}) => {
  const label = getEnvironmentApplicationLabel(application);
  const href = getApplicationHref(hostname);
  const noAccessReason =
    hostname && access === "inaccessible"
      ? "You do not have access"
      : undefined;
  const accessLabel = !hostname
    ? "Endpoint pending"
    : access === "inaccessible"
      ? "No access"
      : access === "public"
        ? "Public"
        : accessAudience === "specified-users"
          ? "Allowed users"
          : "Any authenticated user";
  const disabledTooltipMessage = disabledReason || noAccessReason;
  const disabledLabel = disabledTooltipMessage || accessLabel;
  const disabledAriaLabel = `${label}. ${disabledLabel}${
    /[.!?]$/.test(disabledLabel) ? "" : "."
  }`;
  const AccessIcon = access === "public" ? LockOpenIcon : LockClosedIcon;
  const ready = Boolean(href && access !== "inaccessible" && !disabledReason);
  const rowToneClassName = "text-highlight focus-visible:ring-highlight";
  const readyRowToneClassName = `${rowToneClassName} hover:bg-highlight/10`;
  const triggerClassName =
    variant === "row"
      ? ready
        ? `group flex w-full min-w-0 items-center gap-2 rounded px-2 py-1.5 text-left text-xs font-medium transition duration-150 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0b0b0b] ${readyRowToneClassName}`
        : `flex w-full min-w-0 cursor-not-allowed items-center gap-2 rounded px-2 py-1.5 text-left text-xs font-medium opacity-50 transition duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0b0b0b] ${rowToneClassName}`
      : getComputeCardActionButtonClassName({
          className: `${ready ? "group" : ""} gap-1`,
          disabled: !ready,
        });
  const triggerContents = (
    <>
      <AccessIcon
        className={`${variant === "row" ? "h-4 w-4" : "h-3 w-3"} shrink-0`}
        aria-hidden="true"
      />
      <span className={`${variant === "row" ? "flex-1" : ""} min-w-0 truncate`}>
        {label}
      </span>
      {variant === "row" ? (
        <span className="shrink-0 text-[11px] font-normal text-zinc-500">
          {accessLabel}
        </span>
      ) : null}
      <ArrowTopRightOnSquareIcon
        className={`${variant === "row" ? "h-3.5 w-3.5" : "h-3 w-3"} shrink-0 ${ready ? "opacity-70 transition-opacity group-hover:opacity-100" : "opacity-45"}`}
        aria-hidden="true"
      />
    </>
  );

  return (
    <TooltipPrimitives.Provider delayDuration={150} skipDelayDuration={100}>
      <TooltipPrimitives.Root>
        <TooltipPrimitives.Trigger asChild>
          {ready ? (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              aria-label={`${label}. ${accessLabel}. Opens in a new tab.`}
              onClick={(event) => event.stopPropagation()}
              className={triggerClassName}
            >
              {triggerContents}
            </a>
          ) : (
            <button
              type="button"
              aria-disabled="true"
              aria-label={disabledAriaLabel}
              onClick={(event) => event.stopPropagation()}
              className={triggerClassName}
            >
              {triggerContents}
            </button>
          )}
        </TooltipPrimitives.Trigger>
        <TooltipPrimitives.Portal>
          <TooltipPrimitives.Content
            data-testid={
              disabledTooltipMessage ? "simple-tooltip-content" : undefined
            }
            side="top"
            align="start"
            sideOffset={8}
            collisionPadding={8}
            className={
              disabledTooltipMessage
                ? SIMPLE_TOOLTIP_CONTENT_CLASS_NAME
                : RICH_TOOLTIP_CONTENT_CLASS_NAME
            }
          >
            {disabledTooltipMessage ? (
              disabledTooltipMessage
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <p className="min-w-0 flex-1 break-words font-semibold text-white">
                    {label}
                  </p>
                  {onShare ? (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onShare();
                      }}
                      className="inline-flex shrink-0 items-center rounded border border-transparent bg-highlight px-2 py-1 text-sm font-medium text-black transition hover:bg-highlightDarker focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-highlight dark:text-black"
                    >
                      Share
                    </button>
                  ) : null}
                </div>
                <dl className="mt-2 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1.5">
                  <dt className="text-zinc-400">Port</dt>
                  <dd className="text-right font-mono">{application.port}</dd>
                  <dt className="text-zinc-400">Access</dt>
                  <dd className="text-right">{accessLabel}</dd>
                </dl>
              </>
            )}
            <TooltipPrimitives.Arrow className={TOOLTIP_ARROW_CLASS_NAME} />
          </TooltipPrimitives.Content>
        </TooltipPrimitives.Portal>
      </TooltipPrimitives.Root>
    </TooltipPrimitives.Provider>
  );
};
